import React, { useEffect } from "react";
import Navigation from "./Navigation";

const Contact = () => {
  useEffect(() => {
    document.title = "Hire Me - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      title: "UX/UI Design & Research",
      description: "End-to-end design from research to high-fidelity prototypes",
      features: [
        "User research & testing",
        "Information architecture", 
        "Wireframing & prototyping",
        "Visual design & branding",
        "Design system development",
        "Usability audits"
      ]
    },
    {
      title: "E-commerce Optimization",
      description: "Conversion-focused design for online stores and marketplaces",
      features: [
        "Shopify theme customization",
        "Checkout flow optimization", 
        "Product page design",
        "Mobile commerce UX",
        "Conversion rate analysis",
        "A/B testing setup"
      ]
    },
    {
      title: "Website Redesign",
      description: "Complete website overhauls focused on user experience and business goals",
      features: [
        "Site architecture planning",
        "Responsive design",
        "Content strategy",
        "SEO optimization",
        "Performance improvements",
        "Analytics integration"
      ]
    }
  ];

  const workingStyles = [
    {
      icon: "🏢",
      title: "Full-time Roles",
      description: "Senior UX Designer positions at innovative companies",
      details: [
        "In-house design teams",
        "Product-focused organizations", 
        "E-commerce & SaaS companies",
        "Healthcare & government sectors"
      ]
    },
    {
      icon: "💼",
      title: "Contract Projects", 
      description: "Short-term engagements for specific design challenges",
      details: [
        "Design sprints & workshops",
        "UX audits & recommendations",
        "Prototyping & validation",
        "Design system development"
      ]
    },
    {
      icon: "🤝",
      title: "Consulting",
      description: "Strategic UX guidance and team mentoring",
      details: [
        "UX strategy development",
        "Team process improvements", 
        "Design review & feedback",
        "Junior designer mentoring"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-r from-blue-50 to-indigo-50 section-padding">
        <div className="container-narrow text-center">
          <h1 className="heading-hero text-gray-900 mb-6 animate-fade-in">
            Let&apos;s Work Together
          </h1>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed animate-slide-up">
            I&apos;m passionate about creating user experiences that drive real business results. 
            Whether you need a full-time senior designer or project-specific expertise, 
            let&apos;s discuss how I can help achieve your goals.
          </p>
        </div>
      </section>

      {/* Working Styles */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <h2 className="heading-section text-center mb-12">
            How I Can Help
          </h2>
          
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {workingStyles.map((style, index) => (
              <div key={index} className="card-professional p-8 text-center">
                <div className="text-5xl mb-6">{style.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{style.title}</h3>
                <p className="text-gray-600 mb-6">{style.description}</p>
                
                <ul className="text-left space-y-2 mb-6">
                  {style.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="text-gray-700 text-sm flex items-center">
                      <span className="text-blue-500 mr-2">•</span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="heading-section text-center mb-12">
            Services & Expertise
          </h2>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="card-professional p-8 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">What&apos;s Included:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="text-gray-700 text-sm flex items-center">
                        <span className="text-green-500 mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Me */}
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <h2 className="heading-section text-center mb-12">
            Why Companies Choose Me
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="card-professional p-8 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-3">🎯</span>
                Business-Focused Approach
              </h3>
              <p className="text-gray-700">
                My marketing background means I understand ROI, conversion metrics, and customer psychology. 
                I don&apos;t just make things pretty—I design for measurable business outcomes.
              </p>
            </div>
            
            <div className="card-professional p-8 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-3">⚡</span>
                Fast, Reliable Delivery
              </h3>
              <p className="text-gray-700">
                I work efficiently without compromising quality. Clear communication, 
                realistic timelines, and regular check-ins ensure projects stay on track and exceed expectations.
              </p>
            </div>
            
            <div className="card-professional p-8 bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-3">🔄</span>
                Full-Stack UX Skills
              </h3>
              <p className="text-gray-700">
                From user research to visual design to front-end implementation. 
                I can handle projects end-to-end or integrate seamlessly with your existing team.
              </p>
            </div>
            
            <div className="card-professional p-8 bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-3">👥</span>
                Real Customer Connection
              </h3>
              <p className="text-gray-700">
                My current role keeps me connected to real customers daily. 
                I understand the gap between designed experiences and user reality.
              </p>
            </div>
          </div>
          
          {/* Success Stories */}
          <div className="card-professional p-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
              Recent Project Successes
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">45%</div>
                <div className="font-semibold text-gray-900 mb-1">Faster User Flow</div>
                <div className="text-sm text-gray-600">24 Hour Fitness check-in optimization</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
                <div className="font-semibold text-gray-900 mb-1">On-time Filing Rate</div>
                <div className="text-sm text-gray-600">Koniag Government Services forms</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">300%</div>
                <div className="font-semibold text-gray-900 mb-1">User Growth</div>
                <div className="text-sm text-gray-600">scite_ research platform redesign</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;